import { cn } from "@/lib/utils";
import CheckIcon from "@public/icons/check-icon.svg";
import ExclamationIcon from "@public/icons/exclamation-icon.svg";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

const alertVariants = cva(
  "flex w-full flex-col rounded-lg border p-3 [&_svg]:min-h-5 [&_svg]:min-w-5",
  {
    variants: {
      variant: {
        error: "bg-error text-error-foreground [&_svg]:fill-white",
        success: "bg-primary-5 text-primary-foreground",
        info: "bg-primary-0.5 text-primary-5 [&_svg]:fill-primary-5",
        infoError:
          "bg-error-pale text-error-pale-foreground [&_svg]:fill-error-pale-foreground",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

export type Variants = NonNullable<
  VariantProps<typeof alertVariants>["variant"]
>;

const alertIconByVariant = {
  error: ExclamationIcon,
  success: CheckIcon,
  info: ExclamationIcon,
  infoError: ExclamationIcon,
} as const;

const AlertContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
AlertContainer.displayName = "AlertContainer";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("leading-none font-medium tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("ml-7 text-sm leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { AlertContainer, AlertDescription, alertIconByVariant, AlertTitle };

export const Alert = ({
  title,
  description,
  variant,
  leftIcon,
  className,
}: {
  title: string | React.ReactElement;
  description?: string | React.ReactElement;
  variant?: Variants;
  leftIcon?: React.ReactElement;
  className?: string;
}) => {
  const _variant = variant ?? "info";

  const iconElement =
    leftIcon ?? React.createElement(alertIconByVariant[_variant]);

  return (
    <AlertContainer variant={_variant} className={className}>
      <div className="inline-flex items-center gap-2">
        {iconElement}
        <AlertTitle>{title}</AlertTitle>
      </div>
      <AlertDescription>{description}</AlertDescription>
    </AlertContainer>
  );
};
